import { useEffect, useMemo, useState } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { type EditorType, NEW_EDITOR } from './WorkflowPublicationStatus.types.tsx';
import { getUserWorkflowEditorPreference } from './getUserWorkflowEditorPreference.tsx';

export const useNewWorkflowEditorPreference = (): {
	shouldUseNewWorkFlowEditor: boolean;
	userPreference: EditorType | null | undefined;
	isLoading: boolean;
} => {
	const [editorType, setEditorType] = useState<EditorType | null>();
	const [isLoading, setIsLoading] = useState(true);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		getUserWorkflowEditorPreference()
			.then((pref) => {
				setEditorType(pref);

				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'getDefaultWorkflowEditorPreferance success',
				);
			})
			.catch((error) => {
				setEditorType(null);
				log.safeErrorWithoutCustomerData(
					'workflow-Editor.preference',
					'Unable to fetch workflow editor user preference',
					error,
				);

				fireErrorAnalytics({
					meta: {
						id: 'getDefaultWorkflowEditorPreference',
						packageName: 'jiraWorkflowEditorPreferenceSettings',
					},
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					error: error as Error,
					attributes: {
						isClientFetchError: isClientFetchError(error),
					},
				});
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [createAnalyticsEvent]);

	const shouldUseNewWorkFlowEditor = useMemo(() => {
		if (fg('remove_old_wfe_preference')) {
			return true;
		}

		if (editorType === NEW_EDITOR) {
			return true;
		}

		if (!editorType) {
			return true;
		}

		return false;
	}, [editorType]);

	return { shouldUseNewWorkFlowEditor, userPreference: editorType, isLoading };
};
